import { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";

const UserContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState({
    premium: false,
    business: false,
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const checkRoles = async (currentUser) => {
    if (!currentUser) {
      setRoles({ premium: false, business: false });
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user/checkroles`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const responseData = await response.json();
      setRoles({
        premium: responseData.data.premium || false,
        business: responseData.data.business || false,
      });
    } catch (error) {
      console.error("Error checking roles:", error);
      setRoles({ premium: false, business: false });
    }
  };

  const createUser = async (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const signIn = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => {
    return signOut(auth).then(() => {
      setRoles({ premium: false, business: false });
      navigate("/home");
    });
  };

  useEffect(() => {
    let isMounted = true;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (isMounted) {
        setUser(currentUser);
        await checkRoles(currentUser);
        setLoading(false);
      }
    });
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const value = {
    user,
    roles,
    loading,
    createUser,
    signIn,
    logout,
  };

  return (
    <UserContext.Provider value={value}>
      {!loading && children}
    </UserContext.Provider>
  );
};

export const UserAuth = () => {
  return useContext(UserContext);
};
